import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { authService } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(async (config) => {
  const token = await authService.getIdToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      try {
        const session = await authService.getSession();
        if (session) {
          const token = session.getIdToken().getJwtToken();
          error.config.headers.Authorization = `Bearer ${token}`;
          return api.request(error.config);
        }
      } catch {
        authService.signOut();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const apiClient = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    api.get<T>(url, config).then((res) => res.data),
  post: <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
    api.post<T>(url, data, config).then((res) => res.data),
};

export const costsApi = {
  getOverview: () => apiClient.get<any>('/api/costs/overview'),
  getVentureCosts: (venture: string, days = 30, granularity = 'DAILY') =>
    apiClient.get<any>(`/api/costs/${venture}?days=${days}&granularity=${granularity}`),
  getTrend: (months = 6) =>
    apiClient.get<any>(`/api/costs/trend?months=${months}`),
};

export const stacksApi = {
  listStacks: (venture: string) =>
    apiClient.get<any>(`/api/stacks/${venture}`),
  hibernate: (stackName: string) =>
    apiClient.post<any>(`/api/stacks/${stackName}/hibernate`),
  resume: (stackName: string) =>
    apiClient.post<any>(`/api/stacks/${stackName}/resume`),
};

export const venturesApi = {
  list: () => apiClient.get<any>('/api/ventures'),
};

export const reviewsApi = {
  getLatest: () => apiClient.get<any>('/api/reviews/latest'),
  list: () => apiClient.get<any>('/api/reviews'),
  getById: (id: number) => apiClient.get<any>(`/api/reviews/${id}`),
};

export const usersApi = {
  getMe: () => apiClient.get<any>('/api/users/me'),
};

export default api;
